import * as React from 'react'
import { createComponent, combineRules } from '@cloudflare/style-container'
import { Input } from '@cloudflare/component-input'
import { Icon } from '@cloudflare/component-icon'
import {
  FlexPassthrough,
  FlexPassthroughRule,
} from '@cloudflare/cf-component-flex-passthrough'
import { NativeDropdown } from '@cloudflare/cf-component-native-dropdown'
import { Select } from '@cloudflare/component-select'
import { KeyValEditor, TKeyVals } from '@cloudflare/cf-component-key-val-editor'
import { Textarea } from '@cloudflare/component-textarea'
import { ChangeEvent } from 'react'

/**
 * Like a regular Request, but less restrictive and faster to construct.
 */
export type TEWRequest = {
  url?: string
  method?: 'HEAD' | 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE'
  body?: string
  headers?: TKeyVals
}

export type TRequest = {
  request: TEWRequest
  onChange: (request: TEWRequest) => void
  onSubmit?: () => void
}

export type TRequestEditorProps = {} & TRequest

export type TRequestEditorState = {}

export class RequestEditor extends React.Component<
  TRequestEditorProps,
  TRequestEditorState
> {
  public static defaultProps: Partial<TRequestEditorProps> = {
    request: { method: 'GET', body: '', headers: [] },
  }

  constructor(props: TRequestEditorProps) {
    super(props)

    this.onHeadersChange = this.onHeadersChange.bind(this)
    this.onBodyChange = this.onBodyChange.bind(this)
  }

  render() {
    const { request, onChange, onSubmit } = this.props

    return (
      <FlexPassthrough direction="column">
        <ModulePadding>
          <RequestEditorUrlInput
            request={request}
            onChange={onChange}
            onSubmit={onSubmit}
          />
        </ModulePadding>
        <ModulePadding>
          <ModuleHeading>Headers</ModuleHeading>
          <KeyValEditor
            addNewChildren={() => (
              <span>
                <Icon type="plus" label={'Add New Header'} /> Add New Header
              </span>
            )}
            value={request.headers}
            onChange={this.onHeadersChange}
          />
        </ModulePadding>
        <ModulePaddingFlexPassthrough direction="column">
          <ModuleHeading>Body</ModuleHeading>
          <BodyTextareaWrapper>
            <Textarea
              name="body"
              value={request.body || ''}
              onChange={this.onBodyChange}
              disabled={['GET', 'HEAD'].includes(request.method)}
            />
          </BodyTextareaWrapper>
        </ModulePaddingFlexPassthrough>
      </FlexPassthrough>
    )
  }

  onHeadersChange(headers: TKeyVals) {
    this.props.onChange(Object.assign({}, this.props.request, { headers }))
  }

  onBodyChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    this.props.onChange(Object.assign({}, this.props.request, { body: e.target.value }))
  }
}

const ModulePaddingRule = () => ({
  padding: '0.8rem 0',
  '&:first-child': {
    marginTop: '-0.8rem',
  },
})
const ModulePadding = createComponent(ModulePaddingRule)

const ModulePaddingFlexPassthrough = createComponent(
  combineRules(ModulePaddingRule, FlexPassthroughRule),
)

const ModuleHeading = createComponent(() => ({
  fontSize: '1rem',
  marginBottom: '1rem',
}))

const BodyTextareaWrapper = createComponent(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: '100px',
  '& textarea': {
    flex: 1,
    boxSizing: 'border-box',
    ':disabled': { backgroundColor: theme.colors.gray[8], cursor: 'not-allowed' },
  },
}))

export type TRequestEditorUrlInputProps = {} & TRequest
export class RequestEditorUrlInput extends React.Component<TRequestEditorUrlInputProps> {
  static Wrapper = createComponent(
    ({
      theme,
    }: {
      theme?: any
      onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void
    }) => ({
      display: 'flex',
      flex: 1,
      padding: '1px',
    }),
    'form',
    ['onSubmit'],
  )

  static InputWrapper = createComponent(({ theme }) => ({
    flex: 1,
    '& input': {
      margin: 0,
      boxSizing: 'border-box',
      border: `solid 1px ${theme.colors.gray[6]}`,
      borderLeft: 'none',
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  }))

  constructor(props: TRequestEditorUrlInputProps) {
    super(props)
    this.onUrlChange = this.onUrlChange.bind(this)
    this.onMethodChange = this.onMethodChange.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
  }

  render() {
    const { Wrapper, InputWrapper } = RequestEditorUrlInput
    const { request } = this.props

    return (
      <Wrapper onSubmit={this.onSubmit}>
        {/* <NativeDropdown
          value={request.method}
          onChange={this.onMethodChange}
          isSquareOnRight
        >
          <option value="HEAD">HEAD</option>
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
          <option value="DELETE">DELETE</option>
        </NativeDropdown> */}
        <div style={{ width: '80px' }}>
          <Select
            value={request.method}
            onChange={this.onMethodChange}
            options={[
              { value: 'HEAD', label: 'HEAD' },
              { value: 'GET', label: 'GET' },
              { value: 'POST', label: 'POST' },
              { value: 'PUT', label: 'PUT' },
              { value: 'PATCH', label: 'PATCH' },
              { value: 'DELETE', label: 'DELETE' },
            ]}
          />
        </div>
        <InputWrapper>
          <Input
            name="request-editor-url"
            value={request.url}
            onChange={this.onUrlChange}
          />
        </InputWrapper>
      </Wrapper>
    )
  }

  onMethodChange(
    { value: method }: { value: string } /* e: React.ChangeEvent<HTMLSelectElement> */,
  ) {
    this.props.onChange(Object.assign({}, this.props.request, { method }))
  }

  onUrlChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { request } = this.props
    const url = e.target.value
    this.props.onChange(Object.assign({}, request, { url }))
  }

  onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    this.props.onSubmit()
  }
}



// WEBPACK FOOTER //
// ./node_modules/@cloudflare/cf-edgeworker-http-request-editor/src/RequestEditor.tsx